import React, { useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle, Users, Globe, Award, Target, Heart, Shield } from 'lucide-react';
import SEOHead from './SEOHead';
import { siteContent } from '../data/mockData';

const About = ({ currentSection }) => {
  const content = siteContent.en;

  useEffect(() => {
    if (currentSection) {
      setTimeout(() => {
        const element = document.getElementById(currentSection);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [currentSection]);

  const metrics = [
    { icon: CheckCircle, value: "95%", label: "Faster Time-to-Audit Readiness" },
    { icon: Users, value: "50+", label: "Successful Implementations" },
    { icon: Globe, value: "3", label: "Regulatory Regions" },
    { icon: Award, value: "15+", label: "Years Combined Experience" }
  ];

  return (
    <div id="company" className="py-20 lg:py-32 bg-gradient-to-b from-[#f0f0e8]/30 to-white min-h-screen">
      <SEOHead 
        title="About MedQuore - Medical Device Quality & Compliance Experts"
        description="Learn about MedQuore's mission to lead medical device quality and compliance. Expert team specializing in ISO 13485, EU MDR, and FDA regulatory affairs worldwide."
        keywords="about medquore, medical device quality experts, regulatory affairs team, ISO 13485 specialists, EU MDR consultants, medical device compliance company"
        canonical="https://medquore.com/about"
        ogTitle="About MedQuore - Leading Medical Device Quality Experts"
        ogDescription="Boutique consulting firm specialized in Quality, Regulatory, and Clinical Affairs for medical devices. Delivering audit-ready excellence across US, EU, and MENA."
      />
      <div className="container mx-auto px-4 lg:px-6">
        {/* Page Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-white rounded-full px-4 py-2 mb-6 shadow-sm">
            <Users className="w-4 h-4 text-[#c41927] mr-2" />
            <span className="text-sm font-semibold text-[#011431] uppercase tracking-wide">About MedQuore</span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold text-[#011431] mb-6">
            Senior Expertise. Proven Results.
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Founded by industry veterans with deep regulatory and quality expertise, MedQuore delivers audit-grade solutions 
            that accelerate compliance and reduce time-to-market across US, EU, and MENA regions.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {metrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-[#c41927] to-[#a01520] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-[#011431] mb-2">{metric.value}</div>
                <div className="text-gray-600 font-medium">{metric.label}</div>
              </div>
            );
          })}
        </div>

        {/* Values Section */}
        <div id="values" className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg mb-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#011431] mb-4">Our Values & Mission</h2>
            <p className="text-gray-600 text-lg">The principles that guide every engagement</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Audit-Grade Excellence",
                description: "Every deliverable meets the highest audit standards from day one, ensuring compliance and reducing risk."
              },
              {
                icon: Globe,
                title: "Global Expertise",
                description: "Deep knowledge of US FDA, EU MDR, and MENA regulatory landscapes with local market insights."
              },
              {
                icon: Heart,
                title: "Collaborative Partnership", 
                description: "We work alongside your team, building internal capability while delivering results that last."
              }
            ].map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-14 h-14 bg-[#f0f0e8] rounded-xl flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-7 h-7 text-[#c41927]" />
                  </div>
                  <h3 className="font-bold text-[#011431] mb-3 text-lg">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mission Statement */}
        <div className="text-center bg-gradient-to-r from-[#011431] to-[#c41927] text-white rounded-2xl p-8 lg:p-12 mb-20">
          <Target className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-2xl lg:text-3xl font-bold mb-6">Our Mission</h2>
          <p className="text-lg leading-relaxed max-w-3xl mx-auto opacity-90">
            To accelerate medical device innovation by delivering audit-ready quality and regulatory solutions 
            that reduce time-to-market while ensuring patient safety and regulatory compliance across global markets.
          </p>
        </div>

        {/* Regional Coverage */}
        <div className="text-center">
          <div className="inline-flex items-center bg-[#f0f0e8] rounded-full px-6 py-3">
            <Globe className="w-5 h-5 text-[#c41927] mr-3" />
            <span className="font-semibold text-[#011431]">
              Serving clients across US, EU, and MENA
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;